// packages/frontend/src/components/auth/ProtectedRoute.tsx

import { useEffect } from "react";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import type { UserType } from "@msb/shared";
import { createLogger } from "../../utils/logger";
import { useEnhancedAuthStore } from "../../stores/enhancedAuth";
import { showLogoutDialog } from "./LogoutDialog";

const logger = createLogger("ProtectedRoute");

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectPath?: string;
  requiredRoles?: UserType[];
  fallbackComponent?: React.ReactNode;
}

export function ProtectedRoute({
  children,
  redirectPath = "/login",
  requiredRoles,
  fallbackComponent,
}: ProtectedRouteProps) {
  const {
    isAuthenticated,
    loading,
    currentUser,
    currentTherapist,
    signOut,
    emailVerified,
  } = useAuth();
  const location = useLocation();
  const checkTokenExpiration = useEnhancedAuthStore(
    (state) => state.checkTokenExpiration,
  );

  // Check token expiration on mount and when route changes
  useEffect(() => {
    if (isAuthenticated && !loading) {
      const expirationCheck = checkTokenExpiration();
      if (expirationCheck.expired) {
        logger.warn("Token expired in ProtectedRoute, logging out", {
          component: "ProtectedRoute",
          requestedPath: location.pathname,
        });

        // Show blocking dialog before logging out
        showLogoutDialog().then(() => {
          signOut({ redirectToLogin: true, reason: "expired" }).catch(
            (error) => {
              logger.error("Failed to logout on token expiration", {
                error: error instanceof Error ? error.message : "Unknown error",
              });
            },
          );
        });
      }
    }
  }, [
    isAuthenticated,
    loading,
    location.pathname,
    checkTokenExpiration,
    signOut,
  ]);

  // Wait for auth check to complete
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Verifying authentication...
          </p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    logger.info("User not authenticated, redirecting to login", {
      component: "ProtectedRoute",
      requestedPath: location.pathname,
    });
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // Block every protected route until the account's email is verified
  if (!emailVerified) {
    logger.info("Email not verified, redirecting to verify-email", {
      component: "ProtectedRoute",
      requestedPath: location.pathname,
    });
    return <Navigate to="/verify-email" state={{ from: location }} replace />;
  }

  // Check role-based access control
  if (requiredRoles && currentUser) {
    const userType = currentUser.userType;
    const hasRequiredRole = requiredRoles.includes(userType);

    if (!hasRequiredRole) {
      logger.warn("User does not have required role for this route", {
        component: "ProtectedRoute",
        metadata: {
          userType,
          requiredRoles,
          requestedPath: location.pathname,
        },
      });

      // Show fallback component or redirect to unauthorized page
      if (fallbackComponent) {
        return <>{fallbackComponent}</>;
      }

      return <Navigate to="/unauthorized" state={{ from: location }} replace />;
    }
  }

  // Check if user is a therapist and has therapist data
  if (currentUser?.userType === "therapist" && !currentTherapist) {
    logger.warn("Therapist user missing therapist data", {
      component: "ProtectedRoute",
      metadata: { userId: currentUser.id },
    });

    // This could be a data loading issue, show loading
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Loading therapist data...
          </p>
        </div>
      </div>
    );
  }

  logger.debug("User authenticated and authorized, rendering children", {
    component: "ProtectedRoute",
    userId: currentUser?.id,
    userType: currentUser?.userType,
  });

  return <>{children}</>;
}
